import { useRef, type ChangeEvent } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Avatar, Btn, Icon, IconBtn } from "../ui";
import { Logo } from "./Logo";
import { useAuthStore } from "../../store/useAuthStore";
import { usePlanStore } from "../../store/usePlanStore";
import { TX } from "../../lib/texte";
import { toast } from "../../store/toasts";
import { migriere, dateiBasis } from "@kern";
import { download } from "../../lib/export";

// ============================================================================
// EINE Kopfleiste mit ZWEI Zuständen, nicht zwei Kopfleisten (§5.8.1).
//
//   Navigationsfassung  überall  — Marke, Reiter, Thema, Konto
//   Werkzeugfassung     /plan/:id — Marke, Titelfeld, Werkzeuge, Thema, Konto
//
// Gerüst und Klassen sind die des Fragenbüros (`.kopf`/`.kopf-innen`/
// `.marke`/`.navlink`), der Inhalt ist der des Prototyps (`.tb-title`,
// `.kopf-werkzeuge`). Die Leiste liest den Speicher selbst; die zwei
// optionalen Rückrufe sind der einzige Weg, den es sonst nicht gäbe: `Stände`
// und `Exportieren` öffnen Bauteile, die der EDITOR-Bildschirm hält
// (StaendePanel, ExportModal, §5.6.8/§5.6.10). Ohne sie zeichnet die Leiste
// die beiden Knöpfe gesperrt.
// ============================================================================

export interface TopNavProps {
  /** Editor: öffnet StaendePanel. Fehlt der Rückruf, ist der Knopf gesperrt. */
  onStaende?: () => void;
  /** Editor: öffnet ExportModal (auch an Strg+E, §5.5.3). */
  onExport?: () => void;
}

interface NavPunkt {
  zu: string;
  text: string;
  icon: string;
  /** exakte Übereinstimmung — sonst leuchtet „/" auf jeder Seite */
  genau?: boolean;
}

const PUNKTE: NavPunkt[] = [
  { zu: "/", text: TX.kopf.navPlaene, icon: "calendar", genau: true },
  { zu: "/vorlagen", text: TX.kopf.navVorlagen, icon: "squares-four" },
  { zu: "/papierkorb", text: TX.kopf.navPapierkorb, icon: "trash" },
  { zu: "/claude", text: TX.claude.nav, icon: "info" },
];

function initialenAus(quelle: string): string {
  return (
    quelle
      .replace(/@.*$/u, "")
      .split(/[\s._-]+/u)
      .filter(Boolean)
      .slice(0, 2)
      .map((t) => t.charAt(0).toUpperCase())
      .join("") || "?"
  );
}

export function TopNav({ onStaende, onExport }: TopNavProps = {}) {
  const ort = useLocation();
  const user = useAuthStore((s) => s.auth.user);
  const thema = useAuthStore((s) => s.thema);
  const themaSetzen = useAuthStore((s) => s.themaSetzen);
  const abmelden = useAuthStore((s) => s.abmelden);

  // Feinkörnige Selektoren, nie `usePlanStore()` ohne Selektor: sonst zeichnet
  // die Kopfleiste bei jedem Zug am Balken neu (§5.6).
  const planId = usePlanStore((s) => s.planId);
  const titel = usePlanStore((s) => s.plan?.title ?? "");
  const kannZurueck = usePlanStore((s) => s.undoTiefe > 0);
  const kannVor = usePlanStore((s) => s.redoTiefe > 0);

  const dateiRef = useRef<HTMLInputElement>(null);

  // Werkzeugfassung nur, wenn wirklich ein Plan offen ist: auf dem Weg von
  // /plan/:id zurück zur Liste steht die Adresse einen Wimpernschlag vor dem
  // Speicher, und ein Titelfeld ohne Plan wäre ein leeres Feld ohne Wirkung.
  const imEditor = ort.pathname.startsWith("/plan/") && !!planId;

  const titelSetzen = (wert: string) => {
    // Der Plantitel steht an ZWEI Stellen: in `plan.title` (er wird auf das
    // Blatt gedruckt) und als Listenspalte `titel` (Suche, Sortierung). Das
    // Feld schreibt beide; die Spalte leitet das Backend ohnehin aus
    // `daten.title` ab, das Feld im PUT ist nur die Abkürzung (§5.8.2).
    usePlanStore.getState().metaAendern({ titel: wert });
    usePlanStore.getState().aendern(
      (p) => {
        p.title = wert;
      },
      { marke: "titel", fenster_ms: 500 },
    );
  };

  const jsonOeffnen = (ev: ChangeEvent<HTMLInputElement>) => {
    const datei = ev.target.files?.[0];
    // Das Feld MUSS geleert werden, sonst löst dieselbe Datei beim zweiten
    // Versuch kein `change` aus und der Knopf wirkt kaputt (§5.8.2).
    ev.target.value = "";
    if (!datei) return;
    const leser = new FileReader();
    leser.onload = () => {
      try {
        const neuerPlan = migriere(JSON.parse(String(leser.result)));
        usePlanStore.getState().aendern(() => neuerPlan, { normalisieren: true });
        toast.success(TX.fehler.geladen(datei.name));
      } catch {
        toast.error(TX.fehler.dateiKaputt);
      }
    };
    leser.onerror = () => toast.error(TX.fehler.dateiKaputt);
    leser.readAsText(datei);
  };

  const jsonSpeichern = () => {
    const plan = usePlanStore.getState().plan;
    if (!plan) return;
    // Reihenfolge der Argumente wie in webgis und lib/export.ts: (blob, name).
    download(new Blob([JSON.stringify(plan, null, 2)], { type: "application/json" }), `${dateiBasis(plan.title)}.json`);
  };

  const themaKnopf = (
    <IconBtn
      className="kopf-thema"
      icon={thema === "dark" ? "sun" : "moon"}
      title={thema === "dark" ? TX.kopf.themaHell : TX.kopf.themaDunkel}
      box={32}
      size={16}
      onClick={() => themaSetzen(thema === "dark" ? "light" : "dark")}
    />
  );

  const konto = (
    <div className="reihe" style={{ gap: 4, flex: "none" }}>
      <Avatar initials={initialenAus(user?.anzeigename ?? user?.email ?? "?")} size={26} />
      <IconBtn icon="sign-out" title={TX.kopf.abmelden} box={32} size={16} onClick={() => void abmelden()} />
    </div>
  );

  return (
    <header className="kopf">
      <div className="kopf-innen">
        <Logo />

        {imEditor ? (
          <>
            <input
              className="tb-title"
              value={titel}
              onChange={(ev) => titelSetzen(ev.target.value)}
              aria-label={TX.kopf.titelAria}
              placeholder={TX.kopf.titelPlatzhalter}
            />
            <div className="kopf-werkzeuge">
              <IconBtn
                icon="arrow-counter-clockwise"
                title={TX.kopf.zurueck}
                box={32}
                size={16}
                disabled={!kannZurueck}
                onClick={() => usePlanStore.getState().zurueck()}
              />
              <IconBtn
                icon="arrow-clockwise"
                title={TX.kopf.vor}
                box={32}
                size={16}
                disabled={!kannVor}
                onClick={() => usePlanStore.getState().vor()}
              />
              <IconBtn
                icon="clock-counter-clockwise"
                title={TX.kopf.staende}
                box={32}
                size={16}
                disabled={!onStaende}
                onClick={onStaende}
              />
              <Btn type="button" variant="ghost" size="sm" icon="folder-open" className="mobile-hide" onClick={() => dateiRef.current?.click()}>
                {TX.kopf.oeffnen}
              </Btn>
              <Btn type="button" variant="ghost" size="sm" icon="floppy-disk" className="mobile-hide" onClick={jsonSpeichern}>
                {TX.kopf.speichern}
              </Btn>
              <Btn type="button" variant="primary" size="sm" icon="download-simple" disabled={!onExport} onClick={onExport}>
                {TX.kopf.exportieren}
              </Btn>
              {themaKnopf}
              {konto}
            </div>
            {/* Der Dateiwähler bleibt unsichtbar im Baum — ein sichtbares
                <input type=file> lässt sich nicht gestalten, und der Knopf
                daneben ist der, den man anklickt. */}
            <input ref={dateiRef} type="file" accept="application/json,.json" hidden onChange={jsonOeffnen} />
          </>
        ) : (
          <>
            <nav className="kopf-nav" aria-label={TX.kopf.navAria}>
              {PUNKTE.map((p) => (
                <NavLink
                  key={p.zu}
                  to={p.zu}
                  end={p.genau}
                  title={p.text}
                  className={({ isActive }) => `navlink ring-focus${isActive ? " aktiv" : ""}`}
                >
                  <Icon name={p.icon} size={15} />
                  <span className="navlink-text">{p.text}</span>
                </NavLink>
              ))}
              {user?.rolle === "admin" && (
                <NavLink to="/admin" title={TX.kopf.navVerwaltung} className={({ isActive }) => `navlink ring-focus${isActive ? " aktiv" : ""}`}>
                  <Icon name="sliders" size={15} />
                  <span className="navlink-text">{TX.kopf.navVerwaltung}</span>
                </NavLink>
              )}
            </nav>
            {themaKnopf}
            {konto}
          </>
        )}
      </div>
    </header>
  );
}
